import React, { useCallback, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { Button, Modal, ModalContent } from 'semantic-ui-react';
import CreateAccessPoint from './CreateAccessPoint';
import CreateAccessPointWizard from './CreateAccessPointWizard';
import TemplateType from './TemplateType';
import ViewTemplate from './ViewTemplate';

function CreateAccessPointModal({ showModal, handleClose, projectId, intl }) {
  const [activeStep, setActiveStep] = useState(1);
  const [nextDisabled, setNextDisabled] = useState(false);
  const nextHandlerRef = useRef<(() => void) | null>(null);
  const template = useRef(null);
  const ap = useRef(null);
  const configName = useRef(null);

  const registerStepActions = useCallback((actions: { onNext?: (() => void) | null; nextDisabled?: boolean }) => {
    if (Object.prototype.hasOwnProperty.call(actions, 'onNext')) {
      nextHandlerRef.current = actions.onNext ?? null;
    }
    if (Object.prototype.hasOwnProperty.call(actions, 'nextDisabled')) {
      setNextDisabled(Boolean(actions.nextDisabled));
    }
  }, []);

  const resetWizardState = useCallback(() => {
    nextHandlerRef.current = null;
    template.current = null;
    ap.current = null;
    configName.current = null;
    setNextDisabled(false);
    setActiveStep(1);
  }, []);

  const nextStepHandler = (data) => {
    if (data) {
      const { templateId, apId, apName, templateName } = data;
      template.current = templateId;
      ap.current = apId;
      if (apName) {
        configName.current = apName;
      }
      if (templateName) {
        configName.current = templateName;
      }
    }
    nextHandlerRef.current = null;
    setNextDisabled(false);
    setActiveStep((prevState) => Math.min(prevState + 1, 3));
  };

  const prevStepHandler = () => {
    nextHandlerRef.current = null;
    setNextDisabled(false);
    setActiveStep((prevState) => Math.max(prevState - 1, 1));
  };

  const closeImportDialogue = () => {
    resetWizardState();
    handleClose();
  };

  return (
    <Modal size="large" open={showModal}>
      <Modal.Header>
        {'Create'}
        <Button
          circular
          icon="close"
          title="Close"
          onClick={closeImportDialogue}
          floated="right"
        />
      </Modal.Header>
      <ModalContent>
        <CreateAccessPointWizard step={activeStep}></CreateAccessPointWizard>
        <TemplateType
          customClassName={activeStep === 1 ? '' : 'no-display'}
          handleNextStep={nextStepHandler}
          projectId={projectId}
          registerStepActions={registerStepActions}
        ></TemplateType>
        {activeStep === 2 ? (
          <ViewTemplate
            template={template.current}
            handleNextStep={nextStepHandler}
            customClassName={activeStep === 2 ? '' : 'no-display'}
            registerStepActions={registerStepActions}
          ></ViewTemplate>
        ) : null}
        {activeStep === 3 ? (
          <CreateAccessPoint
            configTypeLabel={
              template.current != null ? 'PMP Equipment' : 'PMP Network Device'
            }
            configTypeId={template.current || ap.current}
            configName={configName.current}
            closeFunc={closeImportDialogue}
            registerStepActions={registerStepActions}
          ></CreateAccessPoint>
        ) : null}
      </ModalContent>
      <Modal.Actions>
        <Button onClick={closeImportDialogue}>Cancel</Button>
        {activeStep > 1 && (
          <Button
            onClick={(e) => {
              e.currentTarget.blur();
              prevStepHandler();
            }}
          >
            Previous
          </Button>
        )}
        <Button
          color={activeStep === 3 ? 'blue' : undefined}
          disabled={nextDisabled}
          onClick={() => nextHandlerRef.current?.()}
        >
          {activeStep === 3 ? 'OK' : 'Next'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default injectIntl(CreateAccessPointModal);
